'use client'

/** Local type for activity history (UserActivity table may not exist in current schema) */
type UserActivity = {
  id: string
  userId: string
  activity: string
  details?: Record<string, unknown> | null
  createdAt: Date
}

type ActivityHistoryProps = {
  activities: UserActivity[]
}

export function ActivityHistory({ activities }: ActivityHistoryProps) {
  const formatActivity = (activity: UserActivity) => {
    const date = new Date(activity.createdAt)
    const timeAgo = getTimeAgo(date)
    
    let description = activity.activity
    let icon = '🔍'
    
    switch (activity.activity) {
      case 'login':
        description = 'Logged in'
        icon = '🔐'
        break
      case 'logout':
        description = 'Logged out'
        icon = '🚪'
        break
      case 'page_view':
        description = `Viewed ${activity.details?.page || 'page'}`
        icon = '👁️'
        break
      case 'api_call':
        description = `API call to ${activity.details?.endpoint || 'endpoint'}`
        icon = '🔌'
        break
      case 'password_change':
        description = 'Changed password'
        icon = '🔑'
        break
      case 'profile_update':
        description = 'Updated profile'
        icon = '✏️'
        break
      default:
        description = activity.activity
    }

    return { description, icon, timeAgo, date }
  }

  const getTimeAgo = (date: Date) => {
    const now = new Date()
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
    
    if (diffInSeconds < 60) return 'Just now'
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`
    return date.toLocaleDateString()
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-foreground-muted text-4xl mb-4">📊</div>
        <h3 className="text-lg font-medium text-foreground-default mb-2">No activity yet</h3>
        <p className="text-foreground-muted">Your activity history will appear here as you use the application.</p>
      </div>
    )
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {activities.map((activity, activityIdx) => {
          const { description, icon, timeAgo, date } = formatActivity(activity)
          const isLast = activityIdx === activities.length - 1
          
          return (
            <li key={activity.id}>
              <div className="relative pb-8">
                {!isLast && (
                  <span
                    className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-border"
                    aria-hidden="true"
                  />
                )}
                <div className="relative flex space-x-3">
                  <div>
                    <span className="h-8 w-8 rounded-full bg-background-muted flex items-center justify-center ring-8 ring-background-secondary">
                      <span className="text-sm">{icon}</span>
                    </span>
                  </div>
                  <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                    <div>
                      <p className="text-sm text-foreground-default">{description}</p>
                      {activity.details && Object.keys(activity.details).length > 0 && (
                        <div className="mt-1 text-xs text-foreground-muted">
                          {activity.details.ipAddress != null && (
                            <span className="mr-4">IP: {String(activity.details.ipAddress)}</span>
                          )}
                          {activity.details.userAgent != null && (
                            <span className="truncate max-w-xs inline-block">
                              {String(activity.details.userAgent)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-right text-sm whitespace-nowrap text-foreground-muted">
                      <time dateTime={date.toISOString()}>{timeAgo}</time>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
